import React from 'react';
import { Box, Typography } from '@mui/material';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import Image from '../../Image/Image';

const KEYPOINT_VECTOR_URL =
  'https://assets.askhippo.co.za/Vectors/KeyPointVector.svg';
const TABLE_MIN_WIDTH = '720px';
const CARD_SECTION_GAP = '32px';
const HEADER_SECTION_GAP = '16px';
const MODULE_SECTION_GAP = '40px';
const IMAGE_CAPTION_GAP = '16px';

const MedicalInsuranceSeoDetailModuleComponent = ({ data }) => {
  const { title, description, footer, cards = [] } = data || {};
  const html = (value) => ({ __html: typeof value === 'string' ? value : '' });

  const renderTable = (table) => {
    if (!table) return null;
    const isLeftAligned = table.align === 'left';

    return (
      <Box
        sx={{
          width: '100%',
          borderRadius: '16px',
          border: '1px solid #E3E6E8',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          boxSizing: 'border-box',
        }}
      >
        <table
          style={{
            width: '100%',
            minWidth: TABLE_MIN_WIDTH,
            borderCollapse: 'collapse',
            tableLayout: 'auto',
          }}
        >
          {table.headers && table.headers.length > 0 && (
            <thead>
              <tr style={{ backgroundColor: '#C7CA00' }}>
                {table.headers.map((header, i) => (
                  <th
                    key={i}
                    style={{
                      padding: '10px 8px',
                      textAlign: isLeftAligned ? 'left' : 'center',
                      verticalAlign: 'middle',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      lineHeight: '1.25rem',
                      letterSpacing: '-0.02em',
                      color: '#162F3B',
                      borderRight:
                        i === table.headers.length - 1
                          ? 'none'
                          : '1px solid #E3E6E8',
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
          )}

          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, colIndex) => (
                  <td
                    key={colIndex}
                    style={{
                      padding: '10px 8px',
                      textAlign: isLeftAligned ? 'left' : 'center',
                      verticalAlign: 'middle',
                      borderTop: '1px solid #E3E6E8',
                      borderRight:
                        colIndex === row.length - 1
                          ? 'none'
                          : '1px solid #E3E6E8',
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#162F3B',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: colIndex === 0 ? 700 : 400,
                        fontStyle: 'normal',
                        fontSize: '0.875rem',
                        lineHeight: '1.25rem',
                        letterSpacing: '-0.002em',
                        m: 0,
                        width: '100%',
                        textAlign: isLeftAligned ? 'left' : 'center',
                        whiteSpace: 'pre-line',
                        wordBreak: 'break-word',
                      }}
                    >
                      {cell}
                    </Typography>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '802px',
          backgroundColor: '#E3E6E8',
          borderRadius: '48px',
          pt: '52px',
          px: '32px',
          pb: '32px',
          gap: MODULE_SECTION_GAP,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: HEADER_SECTION_GAP,
            width: '100%',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '3rem',
              lineHeight: '3.25rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              textAlign: 'center',
              whiteSpace: 'pre-line',
            }}
            component="div"
            dangerouslySetInnerHTML={html(title)}
          />

          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '1.5rem',
              lineHeight: '1.75rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              textAlign: 'center',
              maxWidth: '738px',
              whiteSpace: 'pre-line',
            }}
            component="div"
            dangerouslySetInnerHTML={html(description)}
          />
        </Box>

        {cards.map((card, index) => (
          <ScrollFadeWrapper key={index} delay={index * 0.1}>
            <Box
              sx={{
                width: '1010px',
                backgroundColor: '#FFFFFF',
                borderRadius: '48px',
                boxShadow:
                  '0px 1px 2px 0px #A2ACB133, 0px 12px 24px -4px #162F3B1F',
                boxSizing: 'border-box',
                p: '40px',
                display: 'flex',
                gap: '40px',
              }}
            >
              <Box
                sx={{
                  minWidth: '100px',
                  display: 'flex',
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{ position: 'relative', width: '96px', height: '96px' }}
                >
                  <Image
                    width={96}
                    height={96}
                    src={KEYPOINT_VECTOR_URL}
                    alt="Key point illustration"
                    sx={{ width: '100%', height: '100%' }}
                  />
                  <Typography
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontFamily: 'Pally, Inter',
                      letterSpacing: '-0.03em',
                      fontWeight: 700,
                      fontSize: '4.5rem',
                      lineHeight: '4.25rem',
                      color: '#162F3B',
                      pointerEvents: 'none',
                    }}
                  >
                    {card.number}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  maxWidth: '730px',
                  minWidth: 0,
                  gap: CARD_SECTION_GAP,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.5rem',
                    lineHeight: '1.75rem',
                    color: '#162F3B',
                  }}
                  component="div"
                  dangerouslySetInnerHTML={html(card.title)}
                />

                <Box
                  sx={{
                    fontWeight: 400,
                    fontSize: '1rem',
                    lineHeight: '1.5rem',
                    color: '#162F3B',
                    whiteSpace: 'pre-line',
                    '& a': {
                      color: '#C7CA00',
                      fontWeight: 700,
                      textDecoration: 'none',
                    },
                    '& a:hover': {
                      textDecoration: 'underline',
                    },
                    '& p': { m: 0, mb: '1em' },
                    '& p:last-of-type': { mb: 0 },
                    '& ul': { m: 0, mt: '1em', pl: '20px' },
                    '& li': { mb: '0.5em' },
                  }}
                  component="div"
                  dangerouslySetInnerHTML={html(card.description)}
                />

                {card.image && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: IMAGE_CAPTION_GAP,
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: '#F3F6F6',
                        height: '303px',
                        borderRadius: '16px',
                        width: '100%',
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        lineHeight: '1.25rem',
                        color: '#162F3B',
                      }}
                    >
                      {card.caption}
                    </Typography>
                  </Box>
                )}

                {card.table && renderTable(card.table)}

                {card.table?.secondaryTable ? (
                  <Box sx={{ mt: '24px' }}>
                    {renderTable(card.table.secondaryTable)}
                  </Box>
                ) : null}

                {card.strongText && (
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '1rem',
                      lineHeight: '1.5rem',
                      letterSpacing: '-0.02em',
                      color: '#162F3B',
                    }}
                    component="div"
                    dangerouslySetInnerHTML={html(card.strongText)}
                  />
                )}

                {card.paragraph && (
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: '1rem',
                      lineHeight: '1.5rem',
                      letterSpacing: '-0.02em',
                      color: '#162F3B',
                      whiteSpace: 'pre-line',
                    }}
                    component="div"
                    dangerouslySetInnerHTML={html(card.paragraph)}
                  />
                )}
              </Box>
            </Box>
          </ScrollFadeWrapper>
        ))}

        {footer ? (
          <Typography
            sx={{
              textAlign: 'center',
              fontFamily: 'Inter, sans-serif',
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: '1.5rem',
              letterSpacing: '-0.02em',
              color: '#162F3B',
              maxWidth: '473px',
              mt: '8px',
            }}
          >
            {footer}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default MedicalInsuranceSeoDetailModuleComponent;
